import React from 'react';
import ProgressDot from '../../shared/ProgressDot';
import { shippablePackages, openPackageOf, holdsAnything } from './packageShared';
import './fulfilmentGuide.css';

// The "follow the bouncing ball" guide on Pick, Pack and Ship: a
// Pick → Pack → Ship stepper showing how far the order has got and what's
// waiting at each stage, and a glow on the one button to press next.
//
// A guide, never a constraint: it's worked out fresh from the order as it
// is now, with no memory of steps taken, so doing things out of sequence
// just moves the glow on. It never hides or disables anything — only the
// server's own rules (can't ship an empty package, can't pack ahead of
// picking...) do that, as they always have.
//
// guideFor() returns { message, target }. target names the button to glow
// (message is the same advice in words, kept for the tests and any later
// use):
//   'action-pick' / 'action-pack' — the Pick / Pack choice above the scan
//   'scan'          — scanning is next (the Scan button itself never glows:
//                     it's already the page's main button)
//   'next-stage'    — Proceed to Ship Now
//   'ship'          — Ship Order
//   'stage-pick' / 'stage-pack' / 'stage-ship' — a step on the stepper
//   'scan-another'  — Scan Another Order, once everything's shipped

const STEPS = [
  { key: 'pick', label: 'Pick', field: 'qty_picked' },
  { key: 'pack', label: 'Pack', field: 'qty_packed' },
  { key: 'ship', label: 'Ship', field: 'qty_shipped' },
];

const n = (v) => Number(v || 0);
// What's still meant to go out on a line: ordered less any backorder
// cancelled off it (same as the Fulfilment list's totals).
const orderedQty = (line) => Math.max(0, n(line.qty) - n(line.qty_cancelled));
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;


// Each step's progress in whole SKUs: how many of the order's SKUs are
// fully through that stage, out of how many there are — the same unit as
// "8 SKUs remaining to pick" under it, so done + remaining = total. (Adding
// quantities across SKUs would mix packs, boxes and pieces.) The dot is
// part-filled as soon as any of any SKU is through. Also the Sales Order
// header's progress stepper (SalesOrderStepper.js), so the two agree.
export function stepProgress(order) {
  const lines = (order?.lines || []).filter((l) => orderedQty(l) > 0);
  const total = lines.length;
  return STEPS.map((step) => {
    const done = lines.filter((l) => n(l[step.field]) >= orderedQty(l)).length;
    const started = lines.some((l) => n(l[step.field]) > 0);
    const state = total === 0 || !started ? 'empty' : done >= total ? 'full' : 'partial';
    return { ...step, done, total, state };
  });
}

// mode: the page ('pick' | 'pack' | 'ship'); action: the scan action
// chosen ('pick' | 'pack' | 'put' | 'unpack', or null where the page has
// no scans).
export function guideFor({ mode, order, isStation, action }) {
  const lines = order?.lines || [];
  const total = lines.reduce((sum, l) => sum + orderedQty(l), 0);
  if (!order || total <= 0) return { message: null, target: null };

  const shipped = lines.reduce((sum, l) => sum + Math.min(n(l.qty_shipped), orderedQty(l)), 0);
  if (shipped >= total) return { message: 'Done — everything on this order has shipped.', target: 'scan-another' };

  const linesToPick = lines.filter((l) => n(l.qty_picked) < orderedQty(l)).length;
  const linesToPack = lines.filter((l) => n(l.qty_picked) > n(l.qty_packed)).length;
  const open = openPackageOf(order.packages);
  const openWithSomething = holdsAnything(open) ? open : null;
  const ready = shippablePackages(order.packages);

  if (mode === 'pick') {
    if (linesToPick > 0) {
      if (action !== 'pick') return { message: `${plural(linesToPick, 'SKU', 'SKUs')} left to pick. Choose Pick to carry on.`, target: 'action-pick' };
      return { message: `${plural(linesToPick, 'SKU', 'SKUs')} left to pick. Scan each item, or tap Pick 1 on its card.`, target: 'scan' };
    }
    // Put Back with everything picked is staff stepping off the flow on
    // purpose, so the guide doesn't point onward to Pack while it's chosen.
    if (action === 'put') return { message: null, target: null };
    if (linesToPack > 0) {
      // At a packing station the Pack step opens it there; on a phone the
      // order waits on Pack, so the next thing is the next order.
      return isStation
        ? { message: 'Everything is picked. Pack is next.', target: 'stage-pack' }
        : { message: 'Everything is picked. It waits on Pack for the packing station.', target: 'scan-another' };
    }
    if (ready.length > 0) return { message: 'Everything is picked and packed. Ship is next.', target: 'stage-ship' };
    return { message: 'Everything is picked and packed.', target: null };
  }

  if (mode === 'pack') {
    // The page says so already, and offers nothing to do here.
    if (!isStation) return { message: null, target: null };
    if (linesToPack > 0) {
      if (action !== 'pack') return { message: `${plural(linesToPack, 'SKU', 'SKUs')} picked and waiting to pack. Choose Pack to carry on.`, target: 'action-pack' };
      const into = open ? `into ${open.name}` : 'into a new package';
      return { message: `${plural(linesToPack, 'SKU', 'SKUs')} to pack. Scan each item ${into}.`, target: 'scan' };
    }
    if (open && !openWithSomething) {
      return { message: `${open.name} is open and empty. Put closed packages in with “Into ${open.name}”.`, target: null };
    }
    if (linesToPick > 0 && ready.length === 0) return { message: `Nothing picked is waiting to pack. ${plural(linesToPick, 'SKU', 'SKUs')} still to pick.`, target: 'stage-pick' };
    if (ready.length > 0) {
      const rest = linesToPick > 0 ? ` ${plural(linesToPick, 'SKU', 'SKUs')} still to pick — ship what's packed now, or pick the rest first.` : '';
      if (openWithSomething) return { message: `Everything picked is packed in ${open.name}. Ship it as it is, or close it to start another.${rest}`, target: 'next-stage' };
      return { message: `${plural(ready.length, 'package is', 'packages are')} ready to ship.${rest}`, target: 'next-stage' };
    }
    return { message: null, target: null };
  }

  // Ship Order.
  if (ready.length > 0) {
    const more = ready.length > 1 ? ' Untick any staying behind in the confirmation.' : '';
    return { message: `${plural(ready.length, 'package is', 'packages are')} ready to ship.${more}`, target: 'ship' };
  }
  if (linesToPack > 0) return { message: 'Nothing is packed yet. Pack is next.', target: 'stage-pack' };
  return { message: `Nothing is picked yet. ${plural(linesToPick, 'SKU', 'SKUs')} to pick.`, target: 'stage-pick' };
}

// The class that makes a button glow when it's the guide's target; inset
// for a button inside a clipped, rounded strip.
export function glowClass(target, key, { inset = false } = {}) {
  if (target !== key) return undefined;
  return inset ? 'guide-glow-inset' : 'guide-glow';
}

// Whether each stage has anything ready for it on this order — the same
// tests the stage lists use: Pick, something still to pick; Pack,
// something picked and not yet packed, or a package still open with
// something in it;
// Ship, a package Ship can take.
export function stagesReady(order) {
  const lines = order?.lines || [];
  return {
    pick: lines.some((l) => n(l.qty_picked) < orderedQty(l)),
    pack: lines.some((l) => n(l.qty_picked) > n(l.qty_packed))
      || holdsAnything(openPackageOf(order?.packages)),
    ship: shippablePackages(order?.packages).length > 0,
  };
}

// Everything still to go out is packed, and ready: every SKU fully
// packed and something ready to ship (an open package ships as it is).
// `rest` when part has shipped already, so it's the rest of the order
// that's ready.
export function wholeOrderReady(order) {
  const lines = (order?.lines || []).filter((l) => orderedQty(l) > 0);
  if (!lines.length || shippablePackages(order?.packages).length === 0) return { ready: false, rest: false };
  const ready = lines.every((l) => n(l.qty_packed) >= orderedQty(l));
  return { ready, rest: ready && lines.some((l) => n(l.qty_shipped) > 0) };
}
const wholeReadyText = (whole) => (whole.rest ? 'Rest of order ready' : 'Whole order ready');

// A step with nothing waiting says why: everything's through it ("Fully
// packed"), or what's left is still at the step before ("Waiting on Pick").
const FULLY = { pick: 'Fully picked', pack: 'Fully packed', ship: 'Fully shipped' };
const WAITING_ON = { pack: 'Waiting on Pick', ship: 'Waiting on Pack' };
function notReadyText(step) {
  if (step.done >= step.total) return FULLY[step.key];
  return WAITING_ON[step.key] || FULLY[step.key];
}

// How many SKUs have something waiting at each stage: still to pick,
// picked and not yet packed, packed and not yet shipped (an order has one
// line per SKU). SKUs, not quantities — each is counted in its own unit
// (packs, boxes...), so adding quantities across SKUs means nothing.
function availableAt(order) {
  const lines = order?.lines || [];
  const count = (fn) => lines.filter((l) => fn(l) > 0).length;
  return {
    pick: count((l) => orderedQty(l) - n(l.qty_picked)),
    pack: count((l) => n(l.qty_picked) - n(l.qty_packed)),
    ship: count((l) => n(l.qty_packed) - n(l.qty_shipped)),
  };
}
const skusWord = (count) => `${count} ${count === 1 ? 'SKU' : 'SKUs'}`;
const AVAILABLE = { pick: 'remaining to pick', pack: 'available to pack', ship: 'available to ship' };
// The same, on this page's own step.
const REMAINING = { pick: 'remaining to pick', pack: 'remaining to pack', ship: 'remaining to ship' };
// Ready with nothing loose waiting (a package left open).
const READY_ONLY = { pick: 'Open Pick', pack: 'Packing open', ship: 'Open Ship' };

// Pick → Pack → Ship with each step's progress dot and count. A step with
// something ready for it is a link that opens this order there (onGo),
// and looks it: blue and underlined. A step with nothing ready isn't a
// link at all — greyed, saying why (notReadyText). A link says how much is waiting there
// ("7 available to pack →"). This page's own step is marked.
export function FulfilmentStepper({ order, mode, target, onGo }) {
  const steps = stepProgress(order);
  const ready = stagesReady(order);
  const available = availableAt(order);
  const whole = wholeOrderReady(order);
  // The Ship step says when the whole order's ready, not just some of it.
  const readyText = (key, words) => (key === 'ship' && whole.ready ? wholeReadyText(whole) : words);
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', background: '#fff', border: '1px solid #e3e7ee', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
      {steps.map((step, i) => {
        const here = step.key === mode;
        const link = !here && ready[step.key];
        const labelColor = here ? '#1976d2' : link ? '#1976d2' : '#8a94a6';
        const cellStyle = {
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 4px',
          borderLeft: i === 0 ? 'none' : '1px solid #e3e7ee',
          background: here ? '#eef4fb' : '#fff',
          ...(here ? { boxShadow: 'inset 0 -3px 0 #1976d2' } : {}),
        };
        const content = (
          <>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
              <ProgressDot state={step.state} title={`${step.done} of ${step.total} SKUs fully ${step.label === 'Ship' ? 'shipped' : `${step.label.toLowerCase()}ed`}`} />
              <span style={{ fontWeight: 700, fontSize: 14, color: labelColor, textDecoration: link ? 'underline' : 'none' }}>{step.label}</span>
            </span>
            <span style={{ fontSize: 12, color: here || link ? '#666' : '#a0a8b4', marginTop: 2 }}>{step.done} / {step.total} SKUs</span>
            {here && (
              <span style={{ fontSize: 11, color: step.key === 'ship' && whole.ready ? '#1e7e34' : '#1f2933', fontWeight: step.key === 'ship' && whole.ready ? 700 : 400, marginTop: 2 }}>
                {available[step.key] > 0 ? readyText(step.key, `${skusWord(available[step.key])} ${REMAINING[step.key]}`) : notReadyText(step)}
              </span>
            )}
            {!here && (
              <span style={{ fontSize: 11, fontWeight: link ? 700 : 400, color: link ? (step.key === 'ship' && whole.ready ? '#1e7e34' : '#1976d2') : '#a0a8b4', marginTop: 2 }}>
                {link
                  ? `${readyText(step.key, available[step.key] > 0 ? `${skusWord(available[step.key])} ${AVAILABLE[step.key]}` : READY_ONLY[step.key])} →`
                  : notReadyText(step)}
              </span>
            )}
          </>
        );
        if (!link) {
          return <div key={step.key} style={cellStyle} aria-current={here ? 'step' : undefined}>{content}</div>;
        }
        return (
          <button
            key={step.key}
            type="button"
            onClick={() => onGo(step.key)}
            title={`Open this order on ${step.label}`}
            className={['stepper-link', glowClass(target, `stage-${step.key}`, { inset: true })].filter(Boolean).join(' ')}
            style={{ ...cellStyle, border: 'none', borderLeft: cellStyle.borderLeft, cursor: 'pointer', font: 'inherit' }}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

// Pack's Now panel: the one job in front of the packer and the buttons
// that do it — the main one first, then the valid alternatives (all
// optional, nothing forced; the server's rules still decide what can
// happen). Worked out fresh from the order each time, like guideFor.
// Returns { message, actions: [{ key, label, primary }] }. Keys:
//   'scan-pack' / 'scan-out' — open the camera to Pack / Take Out of
//                     Package
//   'stop-out'      — leave Take Out, back to packing
//   'close-package' — close the open package (its label opens on close)
//   'packing-list'  — the Packing List for the packages ready to ship
//   'proceed-ship'  — Proceed to Ship Now
//   'new-package'   — open an empty package for closed ones to go into
//   'into:<id>'     — put that closed package in the open one
//   'go-pick'       — open the order on Pick
//   'scan-another'  — done, next order
// The panel holds only the step in hand (issue #445): what belongs to one
// package (its label, taking goods out) is on that package's card.
// nextSku: the next line worth packing, named in the message.
// packingListPrinted: the Packing List has been opened for the packages as
// they stand, so it stops leading.
export function packNowFor({ order, action, nextSku, packingListPrinted = false }) {
  const lines = order?.lines || [];
  const total = lines.reduce((sum, l) => sum + orderedQty(l), 0);
  const shipped = lines.reduce((sum, l) => sum + Math.min(n(l.qty_shipped), orderedQty(l)), 0);
  if (!order || total <= 0) return { message: null, actions: [] };
  if (shipped >= total) {
    return { message: 'Everything on this order has shipped.', actions: [{ key: 'scan-another', label: 'Scan Another Order', primary: true }] };
  }

  const linesToPick = lines.filter((l) => n(l.qty_picked) < orderedQty(l)).length;
  const linesToPack = lines.filter((l) => n(l.qty_picked) > n(l.qty_packed)).length;
  const open = openPackageOf(order.packages);
  const openWithSomething = holdsAnything(open) ? open : null;
  const looseClosed = (order.packages || []).filter((p) => !p.open);
  const into = open ? looseClosed.slice(0, 4).map((p) => ({ key: `into:${p.id}`, label: `Put ${p.name} in` })) : [];
  const ready = shippablePackages(order.packages);
  const stillToPick = linesToPick > 0 ? ` ${plural(linesToPick, 'SKU', 'SKUs')} still to pick.` : '';
  const whole = wholeOrderReady(order);

  if (action === 'unpack') {
    return {
      message: 'Taking out: scan an item, or tap Take Out 1 on its card below, to take it out of the open package. It stays picked, ready to pack again.',
      // Started from Take Out Items on the open package's card.
      actions: [{ key: 'scan-out', label: 'Scan to Take Out', primary: true }, { key: 'stop-out', label: 'Back to Packing' }],
    };
  }

  if (linesToPack > 0) {
    const intoText = open ? `into ${open.name}` : 'into a new package';
    return {
      message: `${plural(linesToPack, 'SKU', 'SKUs')} to pack ${intoText}.${nextSku ? ` Next: ${nextSku}.` : ''}`,
      actions: [
        { key: 'scan-pack', label: 'Scan to Pack', primary: true },
        ...(openWithSomething ? [{ key: 'close-package', label: `Close ${open.name}` }] : []),
      ],
    };
  }

  // An open package with nothing in it yet: what's going in it.
  if (open && !openWithSomething && into.length) {
    return {
      message: `${open.name} is open and empty. Put the packages going in it in.`,
      actions: into.map((a, i) => ({ ...a, primary: i === 0 })),
    };
  }

  if (ready.length > 0) {
    if (openWithSomething) {
      return {
        message: whole.ready
          ? `${whole.rest ? 'Rest of order' : 'Whole order'} packed in ${open.name}, ready to ship.`
          : `Part of the order is packed in ${open.name}.${stillToPick}`,
        // Closing comes first: its label opens, then the Packing List
        // and Ship follow from here.
        actions: [
          { key: 'close-package', label: `Close ${open.name}`, primary: true },
          ...into,
        ],
      };
    }
    // The Packing List leads once the whole order is packed and nothing is
    // open, so it can't come before the packages go into an outer one.
    const listFirst = whole.ready && order.offer_packing_list && !packingListPrinted;
    return {
      message: whole.ready
        ? `${whole.rest ? 'The rest of the order' : 'The whole order'} is packed and ready to ship: ${plural(ready.length, 'package', 'packages')}.`
        : `Part of the order is ready to ship: ${plural(ready.length, 'package', 'packages')}.${stillToPick}`,
      actions: [
        ...(listFirst ? [{ key: 'packing-list', label: 'Print Packing List', primary: true }] : []),
        { key: 'proceed-ship', label: 'Proceed to Ship Now', primary: !listFirst },
        ...(looseClosed.length > 1 ? [{ key: 'new-package', label: 'Put in a Package' }] : []),
      ],
    };
  }


  if (linesToPick > 0) {
    return { message: `Nothing picked is waiting to pack.${stillToPick}`, actions: [{ key: 'go-pick', label: 'Go to Pick', primary: true }] };
  }
  return { message: null, actions: [] };
}

// The Now panel: the job in one sentence, the main button full width, the
// alternatives beside each other under it. onAction(key) does each.
export function NowPanel({ now, onAction, busy }) {
  if (!now.message) return null;
  const [primary, ...rest] = [...now.actions.filter((a) => a.primary), ...now.actions.filter((a) => !a.primary)];
  const isShip = (a) => a.key === 'proceed-ship';
  return (
    <div style={{ background: '#fff', border: '2px solid #1976d2', borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#1976d2', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Now</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1f2933', marginBottom: primary ? 12 : 0 }}>{now.message}</div>
      {primary && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onAction(primary.key)}
          style={{ width: '100%', padding: '12px 0', background: isShip(primary) ? '#1e7e34' : '#1976d2', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 15, cursor: busy ? 'wait' : 'pointer' }}
        >
          {primary.label}
        </button>
      )}
      {rest.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {rest.map((a) => (
            <button
              key={a.key}
              type="button"
              disabled={busy}
              onClick={() => onAction(a.key)}
              style={{ flex: '1 1 140px', padding: '9px 12px', background: '#fff', color: isShip(a) ? '#1e7e34' : '#1976d2', border: `1px solid ${isShip(a) ? '#1e7e34' : '#1976d2'}`, borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: busy ? 'wait' : 'pointer' }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
